import { lazy, Suspense, useEffect } from 'react';
import { AuthProvider } from './auth';
import { ConsentBanner } from './components/consent';
import { SplashIntro } from './components/splash-intro';
import { AppShell, StatePanel } from './components/ui';
import { Link, RouterProvider, useRouter } from './router';
import { EXPLAINERS } from './data/seoPages';
import { findPlace } from './data/places';

// Each screen is its own chunk so the map library only loads on routes that draw a map.
const ArchiveScreen = lazy(() => import('./screens/Archive').then((m) => ({ default: m.ArchiveScreen })));
const AuthScreen = lazy(() => import('./screens/Auth').then((m) => ({ default: m.AuthScreen })));
const CaseDetailScreen = lazy(() => import('./screens/CaseDetail').then((m) => ({ default: m.CaseDetailScreen })));
const ObserveScreen = lazy(() => import('./screens/Observe').then((m) => ({ default: m.ObserveScreen })));
const LegalScreen = lazy(() => import('./screens/Legal').then((m) => ({ default: m.LegalScreen })));
const ProfileScreen = lazy(() => import('./screens/Profile').then((m) => ({ default: m.ProfileScreen })));
const ReportScreen = lazy(() => import('./screens/Report').then((m) => ({ default: m.ReportScreen })));
const GuideScreen = lazy(() => import('./screens/Guide').then((m) => ({ default: m.GuideScreen })));
const IdentifyScreen = lazy(() => import('./screens/Identify').then((m) => ({ default: m.IdentifyScreen })));
const ExplainedIndexScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.ExplainedIndexScreen })));
const ExplainerScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.ExplainerScreen })));
const FamousCasesScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.FamousCasesScreen })));
const MeteorShowersScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.MeteorShowersScreen })));
const GlossaryScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.GlossaryScreen })));
const TimelineScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.TimelineScreen })));
const ToolsScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.ToolsScreen })));
const TranchesScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.TranchesScreen })));
const PlacesIndexScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.PlacesIndexScreen })));
const PlaceScreen = lazy(() => import('./screens/SeoPages').then((m) => ({ default: m.PlaceScreen })));
const AlertsSignupScreen = lazy(() => import('./screens/AlertsSignup').then((m) => ({ default: m.AlertsSignupScreen })));
const AlertsScreen = lazy(() => import('./screens/Alerts').then((m) => ({ default: m.AlertsScreen })));
const NewsIndexScreen = lazy(() => import('./screens/News').then((m) => ({ default: m.NewsIndexScreen })));
const NewsPostScreen = lazy(() => import('./screens/News').then((m) => ({ default: m.NewsPostScreen })));
const AdminScreen = lazy(() => import('./screens/Admin').then((m) => ({ default: m.AdminScreen })));
const SiteDetailScreen = lazy(() => import('./screens/SiteDetail').then((m) => ({ default: m.SiteDetailScreen })));
const DataStatusScreen = lazy(() => import('./screens/DataStatus').then((m) => ({ default: m.DataStatusScreen })));

function Routes() {
  const { path } = useRouter();
  useRouteMetadata(path);
  if (path === '/') return <ObserveScreen />;
  if (path === '/archive') return <ArchiveScreen />;
  if (path === '/data-status') return <DataStatusScreen />;
  if (path === '/sites') return <ArchiveScreen initialTab="strategic" />;
  if (path === '/how-to-report-a-ufo') return <GuideScreen />;
  if (path === '/auth') return <AuthScreen />;
  if (path === '/report') return <ReportScreen />;
  if (path === '/identify') return <IdentifyScreen />;
  if (path === '/alerts') return <AlertsSignupScreen />;
  if (path === '/explained') return <ExplainedIndexScreen />;
  if (path === '/famous-ufo-cases') return <FamousCasesScreen />;
  if (path === '/meteor-showers') return <MeteorShowersScreen />;
  if (path === '/glossary') return <GlossaryScreen />;
  if (path === '/ufo-timeline') return <TimelineScreen />;
  if (path === '/tools') return <ToolsScreen />;
  if (path === '/uap-tranches') return <TranchesScreen />;
  if (path === '/ufo-sightings') return <PlacesIndexScreen />;
  const placeMatch = path.match(/^\/ufo-sightings\/([^/]+)$/);
  if (placeMatch) return <PlaceScreen slug={decodeURIComponent(placeMatch[1]).toLowerCase()} />;
  if (path === '/news') return <NewsIndexScreen />;
  const newsMatch = path.match(/^\/news\/([^/]+)$/);
  if (newsMatch) return <NewsPostScreen slug={decodeURIComponent(newsMatch[1])} />;
  if (path === '/admin') return <AdminScreen />;
  const explainedMatch = path.match(/^\/explained\/([^/]+)$/);
  if (explainedMatch) return <ExplainerScreen slug={decodeURIComponent(explainedMatch[1])} />;
  if (path === '/alerts/confirm') return <AlertsScreen action="confirm" />;
  if (path === '/alerts/unsubscribe') return <AlertsScreen action="unsubscribe" />;
  if (path === '/profile') return <ProfileScreen />;
  if (path === '/privacy') return <LegalScreen pageKey="privacy" />;
  if (path === '/terms') return <LegalScreen pageKey="terms" />;
  if (path === '/data-deletion') return <LegalScreen pageKey="data-deletion" />;
  if (path === '/community-guidelines') return <LegalScreen pageKey="community-guidelines" />;
  if (path === '/cookies') return <LegalScreen pageKey="cookies" />;
  if (path === '/support') return <LegalScreen pageKey="support" />;

  const siteMatch = path.match(/^\/sites\/([^/]+)$/);
  if (siteMatch) return <SiteDetailScreen siteId={decodeURIComponent(siteMatch[1])} />;

  const match = path.match(/^\/case\/([^/]+)$/);
  if (match) return <CaseDetailScreen caseNumber={decodeURIComponent(match[1])} />;

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl px-4 py-16">
        <StatePanel dark title="Page not found" message="This route is not part of the Track the Unknown archive." />
        <Link href="/" className="button-primary mt-5">Return to Observe</Link>
      </div>
    </AppShell>
  );
}

const routeDescriptions: Record<string, string> = {
  '/data-status': 'Live data-service checks, provider coverage and limitations for Track the Unknown.',
  '/': 'Live map of UFO and UAP sightings worldwide. Report what you saw, get automatic satellite, planet and weather checks, and find other witnesses near you.',
  '/archive': 'Search public UFO and UAP case files: witness reports, historic cases and unresolved sightings, with analysis notes and related records.',
  '/sites': 'A map of ICBM bases, missile silos and radar stations with recorded UFO incidents: correlation, official explanations and case files.',
  '/how-to-report-a-ufo': 'What to record, where to report it, and how to rule out satellites, planets and aircraft first. A practical 2026 guide for UK and US observers.',
  '/auth': 'Sign in to Track the Unknown to manage sighting alerts, saved cases and submitted reports.',
  '/report': 'Submit a UFO or UAP sighting to the public archive. Add the time, place and what you saw; we check satellites, planets and aircraft automatically.',
  '/identify': 'Answer a few questions and we check your sky at that time and place: planets, bright stars, the ISS, Starlink trains, meteor showers and live aircraft.',
  '/alerts': 'Get a free email when someone reports a UFO or UAP sighting near you. Pick your area and radius; no account needed, one-click unsubscribe.',
  '/explained': 'The everyday things most often reported as UFOs, how to recognise each one, and how to check what was really in your sky.',
  '/famous-ufo-cases': 'Roswell, Rendlesham, the Phoenix Lights and more: what witnesses reported, what investigations concluded, and what remains unexplained.',
  '/meteor-showers': 'The year’s major meteor showers with dates, peak rates and parent bodies, plus tips for watching them without any equipment.',
  '/glossary': 'Plain-English definitions of the terms used in UAP reporting: from acronyms and sensors to the programmes and cases people search for most.',
  '/ufo-timeline': 'Twenty-five moments that shaped the subject: the sightings, the studies, the leaks and the disclosure milestones, from 1947 to today.',
  '/tools': 'Free sky-tracking tools that check satellites, planets, Starlink passes and aircraft against a sighting: plus official reporting links and reading.',
  '/uap-tranches': 'The US government’s PURSUE record releases, tranche by tranche: what each batch of declassified UAP files contains, how the counting works, and how it differs from the National Archives collection.',
  '/ufo-sightings': 'Regional UFO guides: what has been reported in each place, the notable local cases, and the official channels for filing your own report.',
  '/news': 'UFO and UAP news: government disclosure, hearings, new sightings and the science behind them, written up plainly with sources.',
  '/privacy': 'How Track the Unknown collects, stores and protects your data.',
  '/terms': 'The terms of using Track the Unknown, including acceptable use of the public archive.',
  '/data-deletion': 'How to delete your Track the Unknown account and the data tied to it.',
  '/community-guidelines': 'How to write a useful, evidence-first report and what gets removed from the archive.',
  '/cookies': 'What cookies and local storage Track the Unknown uses, and how to control them.',
  '/support': 'Get help with Track the Unknown: accounts, alerts, reports and data requests.',
};

const routeTitles: Record<string, string> = {
  '/': 'Track the Unknown — Live UFO & UAP Sightings Map',
  '/archive': 'UFO Sightings Database & Case Files — Track the Unknown',
  '/sites': 'UFOs and Nuclear Sites: ICBM Bases & Radar — Track the Unknown',
  '/how-to-report-a-ufo': 'How to Report a UFO Sighting (2026 Guide) — Track the Unknown',
  '/auth': 'Observer Access — Track the Unknown',
  '/report': 'Report a Sighting — Track the Unknown',
  '/identify': 'What Was That Light in the Sky? — Track the Unknown',
  '/alerts': 'UFO Sighting Alerts Near You — Track the Unknown',
  '/explained': 'What Was That Light? Common UFO Explanations — Track the Unknown',
  '/famous-ufo-cases': 'Famous UFO Cases: Historic Sightings — Track the Unknown',
  '/meteor-showers': 'Meteor Shower Calendar — Track the Unknown',
  '/glossary': 'UAP and UFO Glossary — Track the Unknown',
  '/ufo-timeline': 'UFO Timeline: 80 Years of Sightings — Track the Unknown',
  '/tools': 'UFO and Skywatching Tools & Resources — Track the Unknown',
  '/uap-tranches': 'US Government UAP File Releases: The PURSUE Tranches — Track the Unknown',
  '/ufo-sightings': 'UFO Sightings by Place: Regional Guides — Track the Unknown',
  '/news': 'UAP News: UFO Disclosure, Sightings & Science — Track the Unknown',
  '/admin': 'Admin — Track the Unknown',
  '/alerts/confirm': 'Sighting Alerts — Track the Unknown',
  '/alerts/unsubscribe': 'Sighting Alerts — Track the Unknown',
  '/profile': 'Observer Record — Track the Unknown',
  '/privacy': 'Privacy Policy — Track the Unknown',
  '/terms': 'Terms of Service — Track the Unknown',
  '/data-deletion': 'Delete Account or Data — Track the Unknown',
  '/community-guidelines': 'Community Guidelines — Track the Unknown',
  '/cookies': 'Cookies & Storage — Track the Unknown',
  '/support': 'Support — Track the Unknown',
};



function useRouteMetadata(path: string) {
  useEffect(() => {
    const isCase = /^\/case\/[^/]+$/.test(path);
    const isSite = /^\/sites\/[^/]+$/.test(path);
    const isNews = /^\/news\/[^/]+$/.test(path);
    const explainer = EXPLAINERS.find((e) => path === `/explained/${e.slug}`);
    const placeMatch = path.match(/^\/ufo-sightings\/([^/]+)$/);
    const place = placeMatch ? findPlace(decodeURIComponent(placeMatch[1]).toLowerCase()) : undefined;
    const known = path in routeTitles || isCase || isSite || isNews || Boolean(explainer) || Boolean(place);
    const title =
      routeTitles[path] ??
      (explainer
        ? `${explainer.seoTitle ?? explainer.title} — Track the Unknown`
        : place
        ? `${place.title} — Track the Unknown`
        : isNews ? 'UAP News — Track the Unknown' : isCase
        ? `${decodeURIComponent(path.split('/').pop() ?? '')} — Track the Unknown`
        : isSite
        ? `${decodeURIComponent(path.split('/').pop() ?? '')} — Nuclear Infrastructure | Track the Unknown`
        : 'Page Not Found — Track the Unknown');
    const description =
      routeDescriptions[path] ??
      (explainer
        ? explainer.description
        : place
        ? place.description
        : isCase || path === '/archive'
        ? 'Explore witness-submitted aerial observation records and clearly labelled contextual analysis.'
        : 'Observe, archive and report unexplained aerial observations with Track the Unknown.');

    document.title = title;
    const canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (canonical) canonical.href = `https://tracktheunknown.com${known ? path : '/'}`;

    for (const selector of ['meta[name="description"]', 'meta[property="og:description"]', 'meta[name="twitter:description"]']) {
      document.querySelector<HTMLMetaElement>(selector)?.setAttribute('content', description);
    }
    document.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute('content', title);
    document.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.setAttribute('content', `https://tracktheunknown.com${known ? path : '/'}`);

    let robots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.append(robots);
    }
    robots.content = known && !['/auth', '/profile', '/report', '/alerts/confirm', '/alerts/unsubscribe', '/admin'].includes(path) ? 'index,follow' : 'noindex,follow';
  }, [path]);
}

export default function App() {
  return (
    <RouterProvider>
      <AuthProvider>
        <Suspense fallback={null}>
          <Routes />
        </Suspense>
        <ConsentBanner />
        <SplashIntro />
      </AuthProvider>
    </RouterProvider>
  );
}
